/**
 * Die Regeln des Moduls: Vollstaendigkeit, Widersprueche, Selbstcheck.
 *
 * Kennt weder DOM noch Supabase noch React.
 *
 * ═══ WOFUER DAS DA IST ════════════════════════════════════════════════════
 * Ein Formular sammelt Angaben. Dieses Modul haelt dagegen. Die Pruefungen
 * hier sind nicht Vollstaendigkeitskontrolle im Sinne von „Feld leer",
 * sondern Widerspruchskontrolle: eine Schutzklasse unter dem Vorschlag ohne
 * Begruendung, ein A2-Filter ohne fluechtigen Stoff, ein Hg-Biomonitoring
 * ohne Hg-Befund, ein Feld, das weder zugeordnet noch abgegrenzt ist.
 *
 * Solche Stellen sind es, die einen Plan formal vollstaendig und fachlich
 * falsch machen. Sie fallen beim Ausfuellen nicht auf, weil nichts leer ist.
 *
 * ═══ WAS DIESES MODUL NICHT TUT ═══════════════════════════════════════════
 * Es beurteilt keine Angemessenheit. Ob die gewaehlte Massnahme fachlich
 * richtig ist, entscheidet der Koordinator; das Werkzeug prueft nur, ob die
 * Angaben zueinander passen. Ein Automat, der Angemessenheit behauptet,
 * waere schlimmer als keiner — man wuerde sich auf ihn verlassen.
 */

import type { AsplanBundle } from './types';
import { EBV_KATALOG_VERSION } from './kataloge/ebvKlassen';
import {
    GEFAHRSTOFF_KATALOG_VERSION, gefahrstoff, istFluechtig, istHautresorptiv, katalogGrenzwert,
} from './kataloge/gefahrstoffe';
import { PRUEFLISTE, PRUEFLISTE_KATALOG_VERSION } from './kataloge/pruefliste';
import { SCHUTZKLASSEN_KATALOG_VERSION } from './kataloge/schutzklassenVorlagen';
import { TRAGEZEIT_KATALOG_VERSION } from './tragezeit';
import { pruefeAbweichung, schutzklasseVorschlag } from './schutzklassen';
import { clusterEinstufung, feldEinstufung } from './einstufung';

/**
 * Fassung aller Kataloge zusammen.
 *
 * Wird beim Anlegen eines Plans festgehalten. Aendert sich EIN Katalog,
 * aendert sich diese Zeichenkette — und ein Bestandsplan traegt weiterhin
 * die Fassung, mit der er beurteilt wurde.
 */
export const aktuelleKatalogVersion = (): string =>
    [
        `ebv:${EBV_KATALOG_VERSION}`,
        `stoffe:${GEFAHRSTOFF_KATALOG_VERSION}`,
        `sk:${SCHUTZKLASSEN_KATALOG_VERSION}`,
        `tragezeit:${TRAGEZEIT_KATALOG_VERSION}`,
        `pruef:${PRUEFLISTE_KATALOG_VERSION}`,
    ].join(' ');

// ═══════════════════════════════════════════════════════════════════════════
// WARNUNGEN
// ═══════════════════════════════════════════════════════════════════════════

export type Schwere =
    /** Steht dem Sinn des Plans entgegen; vor der Freigabe zu klaeren. */
    | 'schwer'
    /** Widerspruch oder Luecke, die begruendet werden sollte. */
    | 'mittel'
    /** Hinweis; oft richtig, manchmal uebersehen. */
    | 'hinweis';

export interface Warnung {
    key: string;
    schwere: Schwere;
    kapitel: string;
    text: string;
    /** Was zu tun ist — nicht nur, was falsch ist. */
    was_tun: string;
}

/**
 * Alle Widersprueche und Luecken des Plans.
 *
 * Reihenfolge: schwer zuerst, dann nach Kapitel. Die Oberflaeche zeigt sie
 * gebuendelt am Kapitel UND gesammelt auf der Startseite des Moduls — wer
 * nur am Kapitel warnt, erreicht denjenigen nicht, der das Kapitel nie
 * aufschlaegt.
 */
export const warnungen = (b: AsplanBundle): Warnung[] => {
    const w: Warnung[] = [];

    w.push(...pruefeGeltungsbereich(b));
    w.push(...pruefeSchutzklassenWahl(b));
    w.push(...pruefeVorbehalte(b));
    w.push(...pruefeTaetigkeiten(b));
    w.push(...pruefeFilter(b));
    w.push(...pruefeGrenzwerte(b));
    w.push(...pruefeMesskonzept(b));
    w.push(...pruefeUnternehmen(b));
    w.push(...pruefeHautschutz(b));

    const rang: Record<Schwere, number> = { schwer: 0, mittel: 1, hinweis: 2 };
    return w.sort((a, z) => rang[a.schwere] - rang[z.schwere] || a.kapitel.localeCompare(z.kapitel));
};

// ── Geltungsbereich ────────────────────────────────────────────────────────

const pruefeGeltungsbereich = (b: AsplanBundle): Warnung[] => {
    const w: Warnung[] = [];
    const zugeordnet = new Set(b.cluster.flatMap(c => c.felder));

    for (const f of b.rasterfelder) {
        if (zugeordnet.has(f.feld_nr)) continue;
        w.push({
            key: `feld_ohne_bereich_${f.feld_nr}`,
            schwere: 'schwer',
            kapitel: 'Arbeitsbereiche',
            text: `Feld ${f.feld_nr} ist keinem Arbeitsbereich zugeordnet und nicht `
                + 'ausdrücklich als nicht A+S-pflichtig abgegrenzt.',
            was_tun: 'Feld einem Arbeitsbereich zuordnen oder einen Bereich der Art '
                + '„abgegrenzt“ anlegen. Ein stillschweigend weggelassener Bereich ist der '
                + 'klassische Haftungsfall.',
        });
    }

    for (const f of b.rasterfelder) {
        const proben = b.proben.filter(p => p.rasterfeld_id === f.id);
        if (proben.length === 0) {
            w.push({
                key: `feld_ohne_probe_${f.feld_nr}`,
                schwere: 'mittel',
                kapitel: 'Rasterfelder und Proben',
                text: `Zu Feld ${f.feld_nr} ist keine Probe erfasst.`,
                was_tun: 'Befunde aus dem Gutachten nachtragen oder das Feld als nicht '
                    + 'untersucht kennzeichnen. Fehlende Proben sind keine Entwarnung.',
            });
            continue;
        }

        const e = feldEinstufung(proben);

        // SCHWER und nicht „mittel": eine Probe ohne uebertragene Einstufung
        // faellt aus dem Maximum heraus, ohne irgendwo aufzutauchen. Ein Feld
        // daneben mit einer einzigen 'BM-0'-Probe hat den Bereich dadurch auf
        // die niedrigste Schutzklasse gezogen — mit Ein-Klick-Uebernahme.
        // Eine Warnung, die die Freigabe nicht anhaelt, hat das nicht
        // verhindert.
        if (e.proben_ohne_einstufung.length > 0) {
            w.push({
                key: `probe_ohne_einstufung_${f.feld_nr}`,
                schwere: 'schwer',
                kapitel: 'Rasterfelder und Proben',
                text: `In Feld ${f.feld_nr} trägt keine Einstufung: `
                    + `${e.proben_ohne_einstufung.join(', ')}.`,
                was_tun: 'EBV-Einstufung aus dem Gutachten übertragen. Ohne sie ist die '
                    + 'Probe nicht beurteilt, und „nicht beurteilt“ ist etwas anderes als '
                    + '„unbelastet“. Bis dahin steht die Schutzklasse des Bereichs auf '
                    + 'unvollständiger Grundlage.',
            });
        }

        // Nur ein Hinweis: die Aussage „durchgaengig belastet" stuetzt sich
        // allein auf die Schichten MIT Tiefe. Eine belastete Probe ohne
        // Tiefenangabe faellt aus dieser Rechnung, und der Bereich bekommt
        // die mittlere statt der hoechsten Klasse vorgeschlagen.
        if (e.proben_ohne_tiefe.length > 0) {
            w.push({
                key: `probe_ohne_tiefe_${f.feld_nr}`,
                schwere: 'hinweis',
                kapitel: 'Rasterfelder und Proben',
                text: `In Feld ${f.feld_nr} ist keine Entnahmetiefe angegeben: `
                    + `${e.proben_ohne_tiefe.join(', ')}.`,
                was_tun: 'Tiefe von/bis nachtragen. Ohne sie zählt die Probe bei der Frage '
                    + 'nach einem unbelasteten Horizont nicht mit. Die Tiefenaussage des '
                    + 'Feldes ist dann schmaler, als sie aussieht.',
            });
        }
    }

    const nebenarbeiten = b.cluster.filter(c => c.art === 'nebenarbeit');
    if (b.cluster.length > 0 && nebenarbeiten.length === 0) {
        w.push({
            key: 'keine_nebenarbeiten',
            schwere: 'hinweis',
            kapitel: 'Arbeitsbereiche',
            text: 'Es ist kein Arbeitsbereich der Art „Nebenarbeit“ angelegt.',
            was_tun: 'Transport, Bereitstellungslager, Wasserhaltung, Abfallaufbereitung, '
                + 'Vermessung und Reinigung prüfen. Sie werden regelmäßig übersehen, '
                + 'exponiert sind die Beschäftigten dort trotzdem.',
        });
    }

    return w;
};

// ── Schutzklassenwahl ──────────────────────────────────────────────────────

const pruefeSchutzklassenWahl = (b: AsplanBundle): Warnung[] => {
    const w: Warnung[] = [];

    for (const c of b.cluster) {
        if (c.art === 'abgegrenzt') continue;

        const felder = c.felder.map(nr => {
            const feld = b.rasterfelder.find(f => f.feld_nr === nr);
            return {
                feld_nr: nr,
                proben: feld ? b.proben.filter(p => p.rasterfeld_id === feld.id) : [],
            };
        });
        const e = clusterEinstufung(felder);
        const vorschlag = schutzklasseVorschlag(c, e, b.schutzklassen);
        const abw = pruefeAbweichung(c, vorschlag, b.schutzklassen);

        if (abw.nach_unten) {
            w.push({
                key: `sk_unter_vorschlag_${c.kennung}`,
                schwere: abw.begruendung_fehlt ? 'schwer' : 'hinweis',
                kapitel: 'Arbeitsbereiche',
                text: `Bereich ${c.kennung}: ${abw.text}`,
                was_tun: abw.begruendung_fehlt
                    ? 'Begründung eintragen oder die vorgeschlagene Klasse übernehmen. Nach '
                      + 'TRGS 524 Nr. 4.1 Abs. 15 gilt bei unzureichender Kenntnis die '
                      + 'maximale Schutzmaßnahme.'
                    : 'Begründung liegt vor. Beim nächsten Befund prüfen, ob sie noch trägt.',
            });
        }

        // Abgegrenzte Bereiche sind oben schon uebersprungen — hier stehen
        // nur noch solche, die eine Klasse brauchen.
        if (!c.schutzklasse_key) {
            w.push({
                key: `sk_fehlt_${c.kennung}`,
                schwere: 'schwer',
                kapitel: 'Arbeitsbereiche',
                text: `Bereich ${c.kennung} hat keine Schutzklasse.`,
                was_tun: vorschlag.key
                    ? `Vorgeschlagen ist ${vorschlag.key}: ${vorschlag.begruendung}`
                    : 'Zuerst die Befunde der zugehörigen Felder erfassen.',
            });
        }
    }

    return w;
};

// ── Vorbehalte ─────────────────────────────────────────────────────────────

const pruefeVorbehalte = (b: AsplanBundle): Warnung[] =>
    b.proben
        .filter(p => p.vorbehalt && !p.wiedervorlage_am)
        .map(p => {
            const feld = b.rasterfelder.find(f => f.id === p.rasterfeld_id);
            return {
                key: `vorbehalt_ohne_wiedervorlage_${p.id}`,
                schwere: 'mittel' as Schwere,
                kapitel: 'Rasterfelder und Proben',
                text: `Probe ${p.mischprobe || '(ohne Bezeichnung)'}`
                    + (feld ? ` in Feld ${feld.feld_nr}` : '')
                    + ' steht unter Analytik-Vorbehalt, hat aber keine Wiedervorlage.',
                was_tun: 'Datum und Verantwortlichen für die Nachprüfung eintragen. Ohne '
                    + 'Wiedervorlage bleibt „Analytik ausstehend“ eine Fußnote, die niemand '
                    + 'wieder anfasst.',
            };
        });

// ── Taetigkeiten ───────────────────────────────────────────────────────────

const pruefeTaetigkeiten = (b: AsplanBundle): Warnung[] => {
    const w: Warnung[] = [];

    const bereicheMitTaetigkeit = new Set(b.taetigkeiten.map(t => t.cluster_kennung));
    for (const c of b.cluster) {
        if (c.art === 'abgegrenzt') continue;
        if (!bereicheMitTaetigkeit.has(c.kennung)) {
            w.push({
                key: `bereich_ohne_taetigkeit_${c.kennung}`,
                schwere: 'schwer',
                kapitel: 'Tätigkeiten',
                text: `Für Bereich ${c.kennung} ist keine Tätigkeit beurteilt.`,
                was_tun: 'Anhang 3 Nr. 5 der DGUV Regel 101-004 verlangt die '
                    + 'tätigkeitsbezogene Beurteilung. Ohne sie springt der Plan vom '
                    + 'Stoffspektrum direkt zu den Schutzmaßnahmen, der häufigste Mangel '
                    + 'überhaupt.',
            });
        }
    }

    for (const t of b.taetigkeiten) {
        if (t.expositionswege.length === 0) {
            w.push({
                key: `taetigkeit_ohne_wege_${t.id}`,
                schwere: 'mittel',
                kapitel: 'Tätigkeiten',
                text: `„${t.taetigkeit}“: kein Expositionsweg beurteilt.`,
                was_tun: 'Inhalativ, dermal und oral je einzeln prüfen. Auch die '
                    + 'Feststellung, dass ein Weg NICHT besteht, ist ein Ergebnis.',
            });
        }
        if (t.exposition === null) {
            w.push({
                key: `taetigkeit_ohne_stufe_${t.id}`,
                schwere: 'mittel',
                kapitel: 'Tätigkeiten',
                text: `„${t.taetigkeit}“: Expositionsabschätzung fehlt.`,
                was_tun: 'Stufe wählen (keine / gering / mittel / hoch). „Keine“ ist ein '
                    + 'zulässiges Ergebnis und erspart Schutzmaßnahmen.',
            });
        }
        if (t.dauer_min === null && !t.haeufigkeit.trim()) {
            w.push({
                key: `taetigkeit_ohne_dauer_${t.id}`,
                schwere: 'hinweis',
                kapitel: 'Tätigkeiten',
                text: `„${t.taetigkeit}“: weder Dauer noch Häufigkeit angegeben.`,
                was_tun: 'Ohne Dauer keine Expositionsabschätzung, und ohne sie keine '
                    + 'begründete Schutzklasse.',
            });
        }
        if (!t.arbeitsverfahren.trim()) {
            w.push({
                key: `taetigkeit_ohne_verfahren_${t.id}`,
                schwere: 'hinweis',
                kapitel: 'Tätigkeiten',
                text: `„${t.taetigkeit}“: Arbeitsverfahren nicht beschrieben.`,
                was_tun: 'Die Wahl des Arbeitsverfahrens bestimmt die Gefährdung. „Aushub '
                    + 'kontaminierten Bodens“ ist keine Verfahrensbeschreibung.',
            });
        }
    }

    return w;
};

// ── Filterwahl ─────────────────────────────────────────────────────────────

/**
 * Prueft den Atemschutzfilter gegen die tatsaechlich gefuehrten Stoffe.
 *
 * Zwei Richtungen, beide praxisrelevant:
 *   • Gasfilteranteil (A2, B …) ohne einen einzigen fluechtigen Stoff — dann
 *     ist er ueberfluessig und suggeriert einen Schutz, den es nicht braucht.
 *   • Quecksilber gefuehrt, aber kein Hg-P3 — der A2-Anteil bindet keinen
 *     Quecksilberdampf. Das ist der gefaehrlichere der beiden Fehler, weil er
 *     wie Vorsicht aussieht.
 */
const pruefeFilter = (b: AsplanBundle): Warnung[] => {
    const w: Warnung[] = [];
    const aktiveStoffe = b.plan.stoffe.filter(s => s.aktiv).map(s => s.key);
    const hatFluechtige = aktiveStoffe.some(istFluechtig);
    const fuehrtHg = aktiveStoffe.includes('hg');

    for (const sk of b.schutzklassen) {
        const typ = (sk.atemschutz.filtertyp ?? '').toUpperCase();
        if (!typ) continue;

        const hatGasanteil = /^[ABEK]\d?/.test(typ);
        if (hatGasanteil && !hatFluechtige) {
            w.push({
                key: `filter_gasanteil_ohne_grund_${sk.key}`,
                schwere: 'hinweis',
                kapitel: 'Schutzklassen',
                text: `${sk.key} sieht Filter ${typ} vor, im Plan ist aber kein flüchtiger `
                    + 'Stoff geführt.',
                was_tun: 'Bei partikelgebundenen Stoffen genügt P3. Ein Gasfilteranteil ist '
                    + 'nur bei nachgewiesenen flüchtigen Begleitstoffen (BTEX, Naphthalin) '
                    + 'erforderlich.',
            });
        }

        if (fuehrtHg && !typ.includes('HG')) {
            const hg = gefahrstoff('hg');
            w.push({
                key: `filter_ohne_hg_${sk.key}`,
                schwere: 'mittel',
                kapitel: 'Schutzklassen',
                text: `Quecksilber ist geführt, ${sk.key} sieht aber Filter ${typ} vor.`,
                was_tun: hg?.filter.begruendung
                    ?? 'Gegen Quecksilberdampf wirkt nur der Quecksilberfilter Hg-P3. Liegt Hg '
                    + 'ausschließlich staubgebunden vor, genügt P3; dann ist das zu '
                    + 'vermerken.',
            });
        }
    }

    return w;
};

// ── Grenzwerte ─────────────────────────────────────────────────────────────

const pruefeGrenzwerte = (b: AsplanBundle): Warnung[] => {
    const w: Warnung[] = [];

    for (const g of b.plan.grenzwerte) {
        const zuordnung = katalogGrenzwert(g.stoff_key, g.art, g.einheit);
        if (zuordnung.mehrdeutig) {
            // Lieber sagen, dass nicht verglichen werden konnte, als den
            // falschen Katalogwert danebenzustellen: die Meldung „der Katalog
            // fuehrt inzwischen X" liest sich als Aufforderung.
            w.push({
                key: `grenzwert_mehrdeutig_${g.stoff_key}_${g.art}`,
                schwere: 'mittel',
                kapitel: 'Gefahrstoffe',
                text: `Der Wert ${g.wert} ${g.einheit} (Art „${g.art}“) lässt sich keinem `
                    + 'Katalogeintrag eindeutig zuordnen. Er wurde nicht gegen den Katalog '
                    + 'geprüft.',
                was_tun: 'Den Wert einmal neu aus dem Stoffkatalog übernehmen. Er stammt aus '
                    + 'einer Zeit, in der derselbe Stoff zwei Werte derselben Art führte '
                    + '(E-/A-Staub, Blei für gebärfähige Frauen).',
            });
            continue;
        }
        const katalog = zuordnung.treffer;
        if (katalog?.veraltet) {
            w.push({
                key: `grenzwert_veraltet_${g.stoff_key}_${g.art}`,
                schwere: 'mittel',
                kapitel: 'Gefahrstoffe',
                text: `Der Wert ${g.wert} ${g.einheit} (${g.quelle}) gilt als überholt.`,
                was_tun: katalog.hinweis ?? 'Nur nachrichtlich führen, nicht als Maßstab.',
            });
            continue;
        }
        if (katalog && katalog.wert !== g.wert) {
            w.push({
                key: `grenzwert_abweichend_${g.stoff_key}_${g.art}`,
                schwere: 'hinweis',
                kapitel: 'Gefahrstoffe',
                text: `Im Plan steht ${g.wert} ${g.einheit}, der Katalog führt inzwischen `
                    + `${katalog.wert} ${katalog.einheit} (${katalog.quelle}).`,
                was_tun: 'Bewusst entscheiden: der Plan behält den Wert, mit dem er '
                    + 'aufgestellt wurde, solange er nicht ausdrücklich aktualisiert wird.',
            });
        }
    }

    const aktive = b.plan.stoffe.filter(s => s.aktiv);
    for (const s of aktive) {
        if (!s.hinweis.trim()) {
            w.push({
                key: `stoff_ohne_begruendung_${s.key}`,
                schwere: 'hinweis',
                kapitel: 'Gefahrstoffe',
                text: `Für ${gefahrstoff(s.key)?.name ?? s.key} ist nicht vermerkt, warum er `
                    + 'im Plan geführt wird.',
                was_tun: 'Die Auswahl ist die fachliche Leistung: ein Stoff gehört in den '
                    + 'Plan, wenn er bei einer Tätigkeit freigesetzt werden kann, nicht, '
                    + 'weil er im Laborbericht steht.',
            });
        }
    }

    return w;
};

// ── Messkonzept ────────────────────────────────────────────────────────────

const pruefeMesskonzept = (b: AsplanBundle): Warnung[] => {
    const w: Warnung[] = [];

    b.plan.messkonzept.forEach((m, i) => {
        if (!m.messziel) {
            w.push({
                key: `messziel_fehlt_${i}`,
                schwere: 'mittel',
                kapitel: 'Messkonzept',
                text: `Für „${m.parameter || `Zeile ${i + 1}`}“ ist kein Messziel benannt.`,
                was_tun: 'Ziel nach TRGS 524 Anlage 9 wählen: Akutgefahr, chronische Wirkung, '
                    + 'Wirksamkeit der Schutzmaßnahmen oder Grenzwerteinhaltung. Eine Messung '
                    + 'ohne Ziel erzeugt nur vorgetäuschte Sicherheit.',
            });
        }
        if (m.messziel === 'akut' && !m.alarmschwelle.trim()) {
            w.push({
                key: `alarm_fehlt_${i}`,
                schwere: 'schwer',
                kapitel: 'Messkonzept',
                text: `„${m.parameter}“ dient der Überwachung von Akutgefahren, hat aber `
                    + 'keine Alarmschwelle.',
                was_tun: 'Ohne Alarmschwelle warnt das Gerät niemanden. Schwelle und die '
                    + 'auszulösende Maßnahme festlegen.',
            });
        }
        if (!m.massnahme.trim()) {
            w.push({
                key: `massnahme_fehlt_${i}`,
                schwere: 'mittel',
                kapitel: 'Messkonzept',
                text: `Für „${m.parameter}“ ist keine auszulösende Maßnahme festgelegt.`,
                was_tun: 'Ohne festgelegte Reaktion ist ein Messwert nur eine Zahl.',
            });
        }
    });

    if (b.plan.messkonzept.length === 0 && b.cluster.some(c => c.art !== 'abgegrenzt')) {
        w.push({
            key: 'messkonzept_leer',
            schwere: 'schwer',
            kapitel: 'Messkonzept',
            text: 'Es ist kein Messkonzept hinterlegt.',
            was_tun: 'Abschnitt 9 der DGUV Regel 101-004 verlangt die messtechnische '
                + 'Überwachung der Arbeitsplatzbedingungen.',
        });
    }

    return w;
};

// ── Unternehmen ────────────────────────────────────────────────────────────

const pruefeUnternehmen = (b: AsplanBundle): Warnung[] => {
    const w: Warnung[] = [];

    for (const u of b.unternehmen) {
        if (u.erklaerung_status !== 'zurueck') {
            w.push({
                key: `erklaerung_offen_${u.id}`,
                schwere: u.erklaerung_status === 'offen' ? 'mittel' : 'hinweis',
                kapitel: 'Unternehmen',
                text: `${u.unternehmen}: Verpflichtungserklärung `
                    + (u.erklaerung_status === 'offen' ? 'noch nicht ausgegeben.' : 'noch nicht zurück.'),
                was_tun: 'Ohne unterschriebene Erklärung darf das Unternehmen nicht in '
                    + 'kontaminierten Bereichen beginnen.',
            });
        }
        if (!u.sachkunde_vorgelegt) {
            w.push({
                key: `sachkunde_offen_${u.id}`,
                schwere: 'mittel',
                kapitel: 'Unternehmen',
                text: `${u.unternehmen}: Sachkundenachweis des Aufsichtführenden liegt nicht vor.`,
                was_tun: 'Nachweis nach DGUV Regel 101-004 Anhang 6A (32 Lehreinheiten) '
                    + 'anfordern. Ohne ihn darf niemand die Aufsicht führen.',
            });
        }
        if (u.arbeitsmediziner_status === 'offen') {
            w.push({
                key: `arbeitsmediziner_offen_${u.id}`,
                schwere: 'hinweis',
                kapitel: 'Unternehmen',
                text: `${u.unternehmen}: betreuender Arbeitsmediziner noch nicht benannt.`,
                was_tun: 'Vom Auftragnehmer vor Arbeitsbeginn benennen lassen.',
            });
        }
    }

    const bgs = b.beteiligte.filter(p => p.funktion_key === 'bg');
    const abgedeckt = new Set(bgs.flatMap(p => p.gilt_fuer));
    for (const u of b.unternehmen) {
        if (bgs.length > 0 && !abgedeckt.has(u.unternehmen)) {
            w.push({
                key: `bg_fehlt_${u.id}`,
                schwere: 'hinweis',
                kapitel: 'Beteiligte',
                text: `${u.unternehmen} ist keiner Berufsgenossenschaft zugeordnet.`,
                was_tun: 'Die Anzeige nach Abschnitt 11.2 geht an die BG des jeweiligen '
                    + 'Unternehmens; auf einer Baustelle können mehrere zuständig sein.',
            });
        }
    }

    return w;
};

// ── Hautschutz ─────────────────────────────────────────────────────────────

const pruefeHautschutz = (b: AsplanBundle): Warnung[] => {
    const hautresorptiv = b.plan.stoffe.filter(s => s.aktiv && istHautresorptiv(s.key));
    if (hautresorptiv.length === 0) return [];

    const hatHautschutz = b.bausteine.some(
        x => x.aktiv && x.kapitel_key.startsWith('hautschutz'));
    if (hatHautschutz) return [];

    return [{
        key: 'hautschutz_fehlt',
        schwere: 'mittel',
        kapitel: 'Hautschutz',
        text: `${hautresorptiv.map(s => gefahrstoff(s.key)?.name ?? s.key).join(', ')} `
            + 'sind hautresorptiv, ein Hautschutzplan ist aber nicht aktiviert.',
        was_tun: 'Hautschutz-, Hautreinigungs- und Hautpflegemittel festlegen und den '
            + 'Hautschutzplan aushängen (TRGS 401, DGUV Regel 112-197).',
    }];
};

// ═══════════════════════════════════════════════════════════════════════════
// SELBSTCHECK GEGEN DIE PRUEFLISTE
// ═══════════════════════════════════════════════════════════════════════════

export interface AutoBefund {
    check_key: string;
    erfuellt: boolean;
    bemerkung: string;
}

/**
 * Beurteilt die automatisch pruefbaren Punkte der Pruefliste.
 *
 * Nur Vorhandensein, nie Angemessenheit. Wo der Katalog `automatisch: false`
 * fuehrt, taucht der Punkt hier gar nicht auf — dort entscheidet der
 * Bearbeiter, und das Werkzeug soll ihm nicht vorgreifen.
 */
export const pruefeAutomatisch = (b: AsplanBundle): AutoBefund[] => {
    const he = b.plan.historische_erkundung ?? {};
    const aktiveStoffe = b.plan.stoffe.filter(s => s.aktiv);
    const befunde: AutoBefund[] = [];

    const setze = (key: string, erfuellt: boolean, bemerkung: string) =>
        befunde.push({ check_key: key, erfuellt, bemerkung });

    setze('check_1',
        !!(he.nutzungsgeschichte && he.nutzungsgeschichte.trim()),
        he.nutzungsgeschichte?.trim() ? 'Nutzungsgeschichte erfasst.' : 'Nutzungsgeschichte fehlt.');

    setze('check_2',
        Array.isArray(he.gebaeudeschadstoffe) && he.gebaeudeschadstoffe.length > 0,
        'Gebäudeschadstoffe (PCB, PAK, Holzschutzmittel) im Feld der Historischen Erkundung.');

    setze('check_3',
        !!(he.untergrund && Object.values(he.untergrund).some(v => String(v ?? '').trim())),
        'Bauwerke, Tanks, Leitungen und Kampfmittel abgefragt.');

    setze('check_6',
        b.rasterfelder.length > 0 && b.proben.some(p => p.tiefe_von !== null),
        'Räumliche Verteilung mit Tiefenlage erfasst.');

    setze('check_9',
        aktiveStoffe.length > 0 && aktiveStoffe.every(s => s.hinweis.trim()),
        'Auswahl der Stoffe begründet.');

    setze('check_10',
        aktiveStoffe.length > 0 && aktiveStoffe.every(s => gefahrstoff(s.key) !== null),
        'Erscheinungsform und Mobilität liegen für alle geführten Stoffe vor.');

    setze('check_11',
        b.plan.grenzwerte.length > 0 && b.plan.grenzwerte.every(g => g.quelle && g.fassung),
        'Grenzwerte mit Quelle und Fassung übernommen.');

    setze('check_12',
        b.cluster.length > 0,
        `${b.cluster.length} Arbeitsbereich(e) angelegt.`);

    setze('check_13',
        b.taetigkeiten.length > 0 && b.taetigkeiten.every(t => t.arbeitsverfahren.trim()),
        'Tätigkeiten mit Arbeitsverfahren beschrieben.');

    setze('check_13b',
        b.cluster.some(c => c.art === 'nebenarbeit'),
        'Nebenarbeiten als eigener Arbeitsbereich geführt.');

    setze('check_13c',
        b.cluster.length > 0 && b.cluster.every(c =>
            c.art === 'abgegrenzt' || Object.keys(c.umgebung ?? {}).length > 0),
        'Umgebungsbedingungen je Arbeitsbereich beschrieben.');

    setze('check_gb1',
        b.taetigkeiten.length > 0 && b.taetigkeiten.every(t => t.expositionswege.length > 0),
        'Expositionswege je Tätigkeit geprüft.');

    setze('check_gb2',
        b.taetigkeiten.length > 0
        && b.taetigkeiten.every(t => t.dauer_min !== null || t.haeufigkeit.trim()),
        'Dauer und Häufigkeit angegeben.');

    setze('check_15',
        b.schutzklassen.length > 0 && b.schutzklassen.every(sk =>
            sk.rang <= 1 || (sk.atemschutz.geraet && sk.schutzkleidung.typ)),
        'PSA nach TRGS 524 Anlage 8 konkret festgelegt.');

    setze('check_16',
        b.schutzklassen.some(sk => sk.handhabung.length > 0),
        'Handhabung, Wechselkriterien und Lagerung geregelt.');

    setze('check_17',
        b.schutzklassen.filter(sk => sk.atemschutz.geraet).every(
            sk => (sk.tragezeit?.ergebnis_min ?? 0) > 0),
        'Tragezeitbegrenzung für alle Klassen mit Atemschutz ermittelt.');

    setze('check_18',
        b.plan.messkonzept.length > 0 && b.plan.messkonzept.every(m => !!m.messziel),
        'Jede Messung trägt ein Ziel nach TRGS 524 Anlage 9.');

    setze('check_19',
        b.plan.messkonzept.length > 0
        && b.plan.messkonzept.every(m => m.geraet.trim() && m.massnahme.trim()),
        'Gerät und auszulösende Maßnahme je Parameter festgelegt.');

    // Nur zurueckgeben, was der Katalog auch als automatisch fuehrt — sonst
    // beurteilt das Werkzeug Punkte, die dem Bearbeiter vorbehalten sind.
    const automatisch = new Set(PRUEFLISTE.filter(p => p.automatisch).map(p => p.key));
    return befunde.filter(x => automatisch.has(x.check_key));
};

// ═══════════════════════════════════════════════════════════════════════════
// FORTSCHRITT
// ═══════════════════════════════════════════════════════════════════════════

export interface Fortschritt {
    /** Schwere Befunde — vor der Freigabe zu klaeren. */
    schwer: number;
    mittel: number;
    hinweise: number;
    /** Automatisch pruefbare Punkte der Pruefliste, die erfuellt sind. */
    checks_erfuellt: number;
    checks_gesamt: number;
    /** Ob der Plan freigegeben werden kann, ohne dass Schweres offen ist. */
    freigabe_moeglich: boolean;
}

export const fortschritt = (b: AsplanBundle): Fortschritt => {
    const w = warnungen(b);
    const checks = pruefeAutomatisch(b);
    const schwer = w.filter(x => x.schwere === 'schwer').length;

    return {
        schwer,
        mittel: w.filter(x => x.schwere === 'mittel').length,
        hinweise: w.filter(x => x.schwere === 'hinweis').length,
        checks_erfuellt: checks.filter(c => c.erfuellt).length,
        checks_gesamt: checks.length,
        freigabe_moeglich: schwer === 0,
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// LEERE STRUKTUREN
// ═══════════════════════════════════════════════════════════════════════════
//
// Ohne diese Vorgaben steht in einem Textfeld irgendwann „null", und eine
// `.map()` auf einer jsonb-Spalte, die als Objekt statt als Liste
// zurueckkommt, stuerzt die Seite ab. Beides ist im Repo schon passiert.

export const leereHistorischeErkundung = () => ({
    nutzungsgeschichte: '',
    quellen: [] as string[],
    erwartete_stoffe: [] as string[],
    gebaeudeschadstoffe: [] as string[],
    untergrund: { bauwerke: '', tanks: '', leitungen: '', kampfmittel: '' },
    luecken: '',
    luecken_folge: '',
    durchgefuehrt_von: '',
    durchgefuehrt_am: '',
});

export const leererGeltungsbereich = () => ({
    felder: [] as string[],
    beginn: '',
    ende_bedingung: '',
    ausgenommen: [] as { bereich: string; begruendung: string }[],
});

export const leerePersonenkreis = () => ({
    haupt: [] as string[],
    untergeordnet: [] as string[],
    alleinarbeit: { zulaessig: false, intervall_min: 15, pna: true },
});

/** Ergaenzt die Aenderungshistorie um einen Eintrag. Die Liste waechst nur;
 *  eine Freigabe friert ein, sie loescht nichts. */
export const ergaenzeHistorie = (
    bisher: { nr: string; datum: string; autor: string; aenderungen: string; rev_label?: string }[],
    eintrag: { datum: string; autor: string; aenderungen: string; rev_label?: string },
) => [...bisher, { nr: String(bisher.length + 1), ...eintrag }];

/**
 * Darf unter diesem Fassungsnamen freigegeben werden? Grund als Satz, sonst
 * `null`.
 *
 * Der Fassungsname ist die Kennung der Freigabe — in der Ablage
 * (`fassung_ref`) wie im Journal des Aenderungsdiensts. Eine zweite Freigabe
 * unter einem schon vergebenen Namen faende dort die alte Fassung: kein
 * eigener Anker, die Ablage meldete „schon abgelegt", und der Hinweis im
 * Modul vergliche gegen den Planungsstand der ERSTEN Freigabe (Codex-Review
 * 17.10.2026). Verglichen wird ohne Raender und ohne Gross/Klein — „rev. 1.0"
 * und „Rev. 1.0" sind dieselbe Kennung fuer jeden, der sie liest.
 */
export const freigabeSperrgrund = (
    historie: ReadonlyArray<{ rev_label?: string | null }> | null | undefined,
    label: string,
): string | null => {
    const gesucht = (label || '').trim().toLowerCase();
    if (!gesucht) return null;
    const vergeben = (historie || []).some(h => (h.rev_label || '').trim().toLowerCase() === gesucht);
    return vergeben
        ? `Der Fassungsname „${label.trim()}" wurde schon für eine Freigabe dieses Plans vergeben. Bitte einen neuen vergeben: `
          + 'jede Freigabe braucht eine eigene Kennung, sonst gilt sie als dieselbe Fassung.'
        : null;
};
